import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { createAppTheme } from './theme';

// 'system' is a real, persisted third state rather than the absence of a
// choice: a user who follows their OS today must keep following it after their
// OS flips, which a resolved 'light'/'dark' snapshot would not do.
export const THEME_MODES = ['system', 'light', 'dark'];
export const DEFAULT_THEME_MODE = 'system';

const STORAGE_KEY = 'cc-theme-mode';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isValidMode = value => THEME_MODES.includes(value);

// Every storage read is guarded: Safari private mode throws on localStorage
// access, and a thrown preference read must not take the whole app down.
export const readStoredThemeMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isValidMode(stored) ? stored : null;
  } catch (error) {
    return null;
  }
};

const writeStoredThemeMode = mode => {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch (error) {
    /* preference is still applied in memory and in Firestore */
  }
};

const prefersDark = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(DARK_QUERY).matches;
};

export const resolveThemeMode = (mode, systemPrefersDark) => {
  if (mode === 'light' || mode === 'dark') return mode;
  return systemPrefersDark ? 'dark' : 'light';
};

const ThemeModeContext = createContext({
  themeMode: DEFAULT_THEME_MODE,
  resolvedMode: 'light',
  setThemeMode: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

export const ThemeModeProvider = ({ children }) => {
  // Seeded from localStorage so a signed-in user's own device renders their
  // theme on the first paint, before the Firestore round-trip resolves.
  const [themeMode, setThemeModeState] = useState(() => readStoredThemeMode() || DEFAULT_THEME_MODE);
  const [systemPrefersDark, setSystemPrefersDark] = useState(prefersDark);
  // Guards the sign-in merge below against clobbering a choice the user makes
  // while the Firestore read is still in flight.
  const localChangeRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const handleChange = event => setSystemPrefersDark(event.matches);

    // addListener is the pre-2021 Safari spelling; addEventListener is absent
    // there and MediaQueryList is not an EventTarget.
    if (query.addEventListener) {
      query.addEventListener('change', handleChange);
      return () => query.removeEventListener('change', handleChange);
    }
    query.addListener(handleChange);
    return () => query.removeListener(handleChange);
  }, []);

  const resolvedMode = resolveThemeMode(themeMode, systemPrefersDark);

  // The attribute drives every --cc-* token in index.css; color-scheme comes
  // along with it so form controls and scrollbars follow too.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedMode);
  }, [resolvedMode]);

  const setThemeMode = useCallback(mode => {
    if (!isValidMode(mode)) return;
    localChangeRef.current = true;
    setThemeModeState(mode);
    writeStoredThemeMode(mode);

    const user = firebase.auth().currentUser;
    if (user) {
      firebase.firestore()
        .collection('userPreferences')
        .doc(user.uid)
        .set({ themeMode: mode }, { merge: true })
        .catch(error => console.error('Error saving theme preference:', error));
    }
  }, []);

  // Firestore is the cross-device record, so on sign-in it wins over whatever
  // this device had stored — except when the stored value is a choice the user
  // just made, which is instead pushed up as the new record.
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(user => {
      if (!user) {
        localChangeRef.current = false;
        return;
      }

      const docRef = firebase.firestore().collection('userPreferences').doc(user.uid);
      docRef.get()
        .then(doc => {
          const remote = doc.exists ? doc.data().themeMode : null;
          if (localChangeRef.current) return;

          if (isValidMode(remote)) {
            setThemeModeState(remote);
            writeStoredThemeMode(remote);
            return;
          }

          // No record yet: seed it from this device so the preference becomes
          // portable, but only if the user actually picked something.
          const local = readStoredThemeMode();
          if (local && local !== DEFAULT_THEME_MODE) {
            docRef.set({ themeMode: local }, { merge: true }).catch(() => {});
          }
        })
        .catch(error => console.error('Error loading theme preference:', error));
    });

    return () => unsubscribe();
  }, []);

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);
  const value = useMemo(
    () => ({ themeMode, resolvedMode, setThemeMode }),
    [themeMode, resolvedMode, setThemeMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeContext;
